// @flow
import * as React from 'react'
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js'
import {
  Form,
  ErrorBodyText,
  CardNumberContainer,
  CardBottomRowItem,
  colors,
  CTAPrimary
} from '@components/shared'
import styled from 'styled-components'
import type { SubscriptionLengthType } from '../../../flowTypes'
import PaymentRequestButton from './PaymentRequestButton'
import SubscriptionName from '@components/subscribe/SubscriptionName'
import { config } from 'react-awesome-styled-grid'
import ThemeContext from '@components/shared/ThemeContext'
import type { AppStateType } from '@reducers/appstate'
import { useSelector } from 'react-redux'

export type PropsType = {
  isExpiring: boolean,
  isSubscribed: boolean,
  inProcess: boolean,
  userLoaded: boolean,
  plan?: SubscriptionLengthType,
  error?: string,
  subscribeText: string,
  trialDays: number,
  price: string,
  successfulPurchase?: boolean
}

export type DispatchPropsType = {
  subscribe: (stripeObj: any, cardNumberElement?: any, altPaymentEvent?: any, isPromoFlow?: string) => void
}

//  https://stripe.com/docs/js/appendix/style
const CARD_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      backgroundColor: colors.transparent,
      color: 'black',
      fontFamily: 'Good Sans Regular, Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '14px',
      fontSmoothing: 'antialiased',
      fontWeight: 500,
      iconColor: 'black',
      '::placeholder': {
        color: 'black'
      },
      ':focus': {
        color: 'black'
      },
      ':-webkit-autofill': {
        color: 'black'
      }
    },
    invalid: {
      iconColor: colors.red,
      color: colors.red
    }
  }
}

// Options for CardExpiry and CardCvc (no iconStyle support)
const CARD_OPTIONS_NO_ICON = {
  style: {
    base: {
      backgroundColor: colors.transparent,
      color: 'black',
      fontFamily: 'Good Sans Regular, Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '14px',
      fontSmoothing: 'antialiased',
      fontWeight: 500,
      '::placeholder': {
        color: 'black'
      },
      ':focus': {
        color: 'black'
      },
      ':-webkit-autofill': {
        color: 'black'
      }
    },
    invalid: {
      color: colors.red
    }
  }
}

export default function BillingInformation (props: PropsType & DispatchPropsType): React.Node {
  const {
    isExpiring,
    inProcess,
    error,
    subscribeText,
    subscribe,
    successfulPurchase
  } = props
  const theme = React.useContext(ThemeContext)
  const ctaText = useSelector((state: AppStateType): string => state.subscriptionText.ctaText)

  const elements = useElements()
  const stripe = useStripe()

  const [submitted, setSubmitted] = React.useState(false)

  React.useEffect((): void => {
    if (props.error && submitted) {
      setSubmitted(false)
    }
  }, [props.error])

  const submit =
    (e: any): void => {
      e.preventDefault()
      const cardNumberElement = elements?.getElement(CardNumberElement)
      if (submitted) {
        return
      }
      if (!cardNumberElement) {
        console.error('Card element not available')
        return
      }

      setSubmitted(true)
      subscribe(stripe, cardNumberElement, null)
    }

  const subscribeButtonText = isExpiring ? 'RENEW SUBSCRIPTION' : ctaText

  const altPaymentSubmit = (altPaymentEvent: any): void => {
    if (submitted) {
      return
    }
    setSubmitted(true)
    subscribe(stripe, null, altPaymentEvent)
  }

  const TermsAndPolicy = <TermsText>
    <a
      style={{
        color: theme === 'pink' ? colors.white : colors.tintOrange
      }}
      target="_blank"
      rel="noopener noreferrer"
      href="https://www.dipseastories.com/terms/dark"
    >
      Terms & Conditions
    </a>
    <a
      style={{
        color: theme === 'pink' ? colors.white : colors.tintOrange
      }}
      target="_blank"
      rel="noopener noreferrer"
      href="https://www.dipseastories.com/privacypolicy/dark"
    >
      Privacy Policy
    </a>

    <a
      style={{
        color: theme === 'pink' ? colors.white : colors.tintOrange
      }}
      target="_blank"
      rel="noopener noreferrer"
      href="https://www.dipseastories.com/paymentterms/dark/"
    >
      Payment Terms
    </a>
  </TermsText>

  return (
    <SubscriptionModalContainer>
      <SubModalPaymentContainer>
        <Form padding='0' autoComplete="on">
          <CardInputContainer>
            <SubscriptionNameContainer>
              <SubscriptionName />
            </SubscriptionNameContainer>
            <CardNumberContainer>
              <CardNumberElement options={CARD_OPTIONS} className={'dipsea-card-element'}/>
            </CardNumberContainer>
            <CardBottomRow>
              <CardBottomRowItem margin={'0 5px 0 0'}>
                <CardExpiryElement options={CARD_OPTIONS_NO_ICON} />
              </CardBottomRowItem>
              <CardBottomRowItem margin={'0 0 0 5px'}>
                <CardCvcElement options={CARD_OPTIONS_NO_ICON} />
              </CardBottomRowItem>
            </CardBottomRow>
            <ErrorContainer>
              <ErrorBodyText color={colors.white} showError={!!error}>{error}</ErrorBodyText>
            </ErrorContainer>
          </CardInputContainer>
          <CTAPrimary
            id="checkout-submit"
            className="subscribe-cta"
            type="submit"
            onClick={submit}
            disabled={inProcess || successfulPurchase || submitted}
            color={colors.white}
            backgroundColor={colors.orange}
            fontWeight={'bold'}
            disabledColor={colors.white}
            disabledBackgroundColor={colors.violet}
            disabledBorderColor={colors.white}
            borderRadius={'32px'}
            borderColor={colors.transparent}
            height={'40px'}
            width={'100%'}
            hoverColor={'rgba(255,255,255, 0.5)'}
          >
            {inProcess ? 'Processing' : subscribeButtonText}
          </CTAPrimary>

          <PaymentRequestButton
            stripe={stripe}
            price={props.price}
            plan={props.plan}
            trialDays={props.trialDays}
            subscribe={(altPaymentEvent: any): void => altPaymentSubmit(altPaymentEvent)}
          />
        </Form>
        <SubscribTextContainer>
          {subscribeText}
        </SubscribTextContainer>
        {TermsAndPolicy}
      </SubModalPaymentContainer>
    </SubscriptionModalContainer>
  )
}
const SubscribTextContainer = styled.div`
   text-align: center;
   font-size: 12px;
   line-height: 18px;
   font-family: 'Good Sans Light';
   color: ${colors.white};
   margin: 20px 0;
  ${(props: any): string => config(props).media.lg`
    text-align: left;
  `}
`
const ErrorContainer = styled.div`
  bottom: -1.25em;
  left: 0;
  right: 0;
  max-height: 20px;
  position: absolute;
`

const CardInputContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  margin: 0 0 25px 0;
  position: relative;
`
const CardBottomRow = styled.div`
  display: flex;
  flex-direction: row;
`
const SubscriptionModalContainer = styled.div`
  width: 100%;
`
const SubModalPaymentContainer = styled.div`
  margin: 0 auto;

  p {
    ${(props: any): string => config(props).media.lg`
    text-align: left;
  `}
  }
`
const TermsText = styled.p`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.orange};
    font-family: 'Good Sans Regular';
    font-size: 12px;
    font-style: normal;
    font-weight: normal;
    line-height: 1.5em;
    margin: 0;
    padding: 0;
    text-align: center;
    width: 100%;
    margin-top: 20px;
    a {
        margin-left: 16px;
        border-radius: 5px;
        :hover {
            opacity: 0.5;
        }
    }
`

export const SubscriptionNameContainer = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
`
